package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.CHARS_PER_CREDIT
import dev.lingvo.shared.MAX_MT_SOURCE_CHARS
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.creditsForString
import dev.lingvo.site.components.ProviderMark
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Text

/** Actual shared-key charges, using the same bounded source lengths as the shared billing model. */
@Composable
internal fun CreditExamples() {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            LSectionTitle(t("app.settings.billing.credit_examples.what_does_a_credit_buy", "What does a credit buy?"), Modifier.fontSize(TypeScale.HeadingSmall))
            LMuted(t("app.settings.billing.credit_examples.credits_pay_for_machine_translation_using", "Credits pay for machine translation using Lingvo's shared provider key. Each string is charged per target language, by source length and engine."))
            LTable(
                caption = t("app.settings.billing.credit_examples.credit_cost_examples", "Credit cost examples"),
                tableModifier = Modifier.styleModifier {
                    property("table-layout", "fixed")
                    property("min-width", "0")
                },
            ) {
                LTableHead {
                    LTableRow {
                        LTableHeaderCell(creditCellModifier()) { Text(t("app.settings.billing.credit_examples.source_length", "Source length")) }
                        LTableHeaderCell(creditCellModifier()) { Text(t("app.settings.billing.credit_examples.standard_engines", "DeepL / Google")) }
                        LTableHeaderCell(creditCellModifier()) { Text(t("app.settings.billing.credit_examples.claude_openai", "Claude / OpenAI")) }
                    }
                }
                LTableBody {
                    listOf(CHARS_PER_CREDIT, MAX_MT_SOURCE_CHARS).forEach { characters ->
                        LTableRow {
                            LTableHeaderCell(creditCellModifier(), scope = LTableHeaderScope.ROW) {
                                Text(
                                    if (characters == CHARS_PER_CREDIT) {
                                        tf("app.settings.billing.plans.short_length", "Up to {count} characters", "count" to formatCount(characters))
                                    } else {
                                        tf(
                                            "app.settings.billing.plans.long_length",
                                            "{start}–{end} characters",
                                            "start" to formatCount(CHARS_PER_CREDIT + 1),
                                            "end" to formatCount(characters),
                                        )
                                    },
                                )
                            }
                            LTableCell(creditCellModifier()) {
                                Text(creditExampleCost(MtProvider.DEEPL, characters))
                            }
                            LTableCell(creditCellModifier()) {
                                Text(creditExampleCost(MtProvider.CLAUDE, characters))
                            }
                        }
                    }
                }
            }
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LMuted(t("app.settings.billing.credit_examples.standard_engines", "DeepL / Google"))
                Row(Modifier.gap(Space.LG).flexWrap(FlexWrap.Wrap)) {
                    listOf(MtProvider.DEEPL, MtProvider.GOOGLE).forEach { CreditProvider(it) }
                }
                LMuted(t("app.settings.billing.credit_examples.claude_and_openai_use_the_higher", "Claude and OpenAI use the higher rate"))
                Row(Modifier.gap(Space.LG).flexWrap(FlexWrap.Wrap)) {
                    CreditProvider(MtProvider.CLAUDE)
                    CreditProvider(MtProvider.OPENAI)
                }
                LMuted(t("app.settings.billing.credit_examples.no_lingvo_credit_providers", "LibreTranslate and MyMemory use no Lingvo credits. Their service limits and setup requirements still apply."))
                Row(Modifier.gap(Space.LG).flexWrap(FlexWrap.Wrap)) {
                    CreditProvider(MtProvider.LIBRETRANSLATE)
                    CreditProvider(MtProvider.MYMEMORY)
                }
            }
            LMuted(
                tf(
                    "app.settings.billing.plans.credit_limits",
                    "The source limit is {limit} characters per string. Plurals use the longest form, not the sum. Translating into two languages costs twice the per-language amount.",
                    "limit" to formatCount(MAX_MT_SOURCE_CHARS),
                ),
            )
            LMuted(t("app.settings.billing.credit_examples.your_own_provider_key_uses_no", "Your own provider key uses no Lingvo credits; your provider may bill you directly. Shared-key availability depends on provider setup and readiness."))
            LMuted(t("app.settings.billing.credit_examples.included_and_granted_credits_reset_on", "Included and granted credits reset on the 1st of each month (UTC). If they run out, shared-key translation stops; there are no automatic overage charges. Manual translation, import, export, review and QA remain available."))
        }
    }
}

private fun creditCellModifier(): Modifier = Modifier.styleModifier {
    property("white-space", "normal")
    property("overflow-wrap", "anywhere")
    property("padding", Space.SM.toString())
}

private fun creditExampleCost(provider: MtProvider, characters: Int): String = tPlural(
    "app.settings.billing.plans.count.example_credits",
    creditsForString(provider, characters),
    "{count} credit",
    "{count} credits",
)

@Composable
private fun CreditProvider(provider: MtProvider) {
    Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
        ProviderMark(provider, size = 24)
        Text(provider.displayName)
    }
}
